import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { cn } from '@/lib/utils'
import { easeInOutQuart, easeOutExpo } from '@/lib/motion'
import { useLenis } from '@/components/generals/smooth-scroll'
import { useTone } from '@/components/generals/tone'
import { useHeaderState } from '@/components/generals/header-state'
import { TransitionLink } from '@/components/generals/transition-link'
import { LocalTime } from '@/components/generals/local-time'
import { navigation } from '@/constants/site'
import { photographs } from '@/content/photographs'
import { profile, socials } from '@/content/profile'

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'

    return pathname === href || pathname.startsWith(`${href}/`)
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const { mode } = useTone()
    const { scrollY } = useScroll()
    const { tucked, setTucked } = useHeaderState()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useMotionValueEvent(scrollY, 'change', current => {
        const previous = scrollY.getPrevious() ?? 0
        const nextTucked = current > previous && current > 180

        if (nextTucked !== tucked) setTucked(nextTucked)
        setScrolled(current > 40)
    })

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (open) {
            lenis?.stop()
            document.body.style.overflow = 'hidden'
        }
        else {
            lenis?.start()
            document.body.style.overflow = ''
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open, lenis])

    const light = mode === 'light' && !open

    return (
        <>
            <a
                href='#main'
                className='fixed left-4 top-4 z-[150] -translate-y-24 bg-ember px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-peat transition-transform focus-visible:translate-y-0'
            >
                Skip to content
            </a>

            <motion.header
                initial={false}
                animate={{ y: tucked && !open ? '-110%' : '0%' }}
                transition={{ duration: 0.6, ease: easeOutExpo }}
                className={cn(
                    'fixed inset-x-0 top-0 z-[100] transition-[color,background-color,backdrop-filter] duration-700',
                    light ? 'text-ink' : 'text-bone',
                    scrolled && !open && (light ? 'bg-paper/70 backdrop-blur-md' : 'bg-peat/60 backdrop-blur-md')
                )}
            >
                <div className='grid grid-cols-[1fr_auto] items-center gap-6 px-5 py-4 md:grid-cols-[1fr_auto_1fr] md:px-8 md:py-5'>
                    <TransitionLink
                        href='/'
                        className='group flex items-baseline gap-3 justify-self-start'
                        aria-label={`${profile.name}, home`}
                    >
                        <span className='font-heading text-[19px] leading-none tracking-[-0.01em] md:text-[21px]'>
                            Arian Mavriqi
                        </span>

                        <span className='hidden font-mono text-[10px] uppercase tracking-[0.2em] opacity-60 transition-opacity group-hover:opacity-100 lg:inline'>
                            Wildlife &amp; nature
                        </span>
                    </TransitionLink>

                    <div className='hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] opacity-70 md:flex'>
                        <span>{profile.baseLocal}</span>
                        <span className='h-px w-6 bg-current opacity-50' />
                        <LocalTime />
                    </div>

                    <nav aria-label='Primary' className='hidden justify-self-end md:block'>
                        <ul className='flex items-center gap-8'>
                            {navigation.filter(item => item.href !== '/').map(item => {
                                const active = isActive(router.pathname, item.href)

                                return (
                                    <li key={item.href}>
                                        <TransitionLink
                                            href={item.href}
                                            aria-current={active ? 'page' : undefined}
                                            className='group relative flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em]'
                                        >
                                            <span
                                                className={cn(
                                                    'size-[5px] rounded-full bg-ember transition-transform duration-500',
                                                    active ? 'scale-100' : 'scale-0 group-hover:scale-100'
                                                )}
                                            />
                                            {item.label}

                                            {item.href === '/archive' && (
                                                <sup className='text-[9px] opacity-60'>{photographs.length}</sup>
                                            )}
                                        </TransitionLink>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='site-menu'
                        className='flex h-10 items-center gap-2 justify-self-end font-mono text-[11px] uppercase tracking-[0.18em] md:hidden'
                    >
                        <span className='relative block h-2.5 w-5'>
                            <span
                                className={cn(
                                    'absolute left-0 h-px w-full bg-current transition-transform duration-500',
                                    open ? 'top-1/2 rotate-45' : 'top-0'
                                )}
                            />
                            <span
                                className={cn(
                                    'absolute left-0 h-px w-full bg-current transition-transform duration-500',
                                    open ? 'top-1/2 -rotate-45' : 'top-full'
                                )}
                            />
                        </span>
                        {open ? 'Close' : 'Menu'}
                    </button>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        id='site-menu'
                        key='menu'
                        role='dialog'
                        aria-modal='true'
                        aria-label='Site menu'
                        initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                        animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                        exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                        transition={{ duration: 0.75, ease: easeInOutQuart }}
                        className='fixed inset-0 z-[95] flex flex-col justify-between bg-peat px-5 pb-8 pt-24 text-bone md:hidden'
                        data-lenis-prevent
                    >
                        <nav aria-label='Mobile'>
                            <ul className='flex flex-col'>
                                {navigation.map((item, index) => {
                                    const active = isActive(router.pathname, item.href)

                                    return (
                                        <li key={item.href} className='overflow-hidden border-b border-bone/10'>
                                            <motion.div
                                                initial={{ y: '110%' }}
                                                animate={{ y: '0%' }}
                                                transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.25 + index * 0.06 }}
                                            >
                                                <TransitionLink
                                                    href={item.href}
                                                    aria-current={active ? 'page' : undefined}
                                                    className='flex items-baseline justify-between py-3'
                                                >
                                                    <span className={cn('font-heading text-[15vw] leading-[0.95] tracking-[-0.03em]', active && 'italic')}>
                                                        {item.label}
                                                    </span>
                                                    <span className='font-mono text-[11px] tracking-[0.2em] text-fog'>
                                                        0{index + 1}
                                                    </span>
                                                </TransitionLink>
                                            </motion.div>
                                        </li>
                                    )
                                })}
                            </ul>
                        </nav>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.55 }}
                            className='grid grid-cols-2 gap-6 font-mono text-[10px] uppercase tracking-[0.18em] text-fog'
                        >
                            <ul className='flex flex-col gap-2'>
                                {socials.slice(0, 3).map(social => (
                                    <li key={social.href}>
                                        <a href={social.href} target='_blank' rel='noreferrer' className='text-bone'>
                                            {social.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            <div className='flex flex-col gap-2 text-right'>
                                <span>{profile.coordinates}</span>
                                <span>{profile.baseLocal}, Kosovo</span>
                                <LocalTime className='text-bone' />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}
